import { PayloadAction } from '@reduxjs/toolkit';
import { all, call, put, select, takeLatest } from 'redux-saga/effects';
import tasksApiService, { TaskResponse } from '../api/tasks-api';
import {
  fetchTasksRequest,
  fetchTasksSuccess,
  fetchTasksFailure,
  fetchTaskByIdRequest,
  fetchTaskByIdSuccess,
  fetchTaskByIdFailure,
  createTaskRequest,
  createTaskSuccess,
  createTaskFailure,
  updateTaskRequest,
  updateTaskSuccess,
  updateTaskFailure,
  deleteTaskRequest,
  deleteTaskSuccess,
  deleteTaskFailure,
  changeTaskStatusRequest,
  changeTaskStatusSuccess,
  changeTaskStatusFailure,
  changeTaskPriorityRequest,
  changeTaskPrioritySuccess,
  changeTaskPriorityFailure,
  fetchStatsRequest,
  fetchStatsSuccess,
  fetchStatsFailure,
  fetchOverdueTasksRequest,
  fetchOverdueTasksSuccess,
  fetchOverdueTasksFailure,
  reassignTaskRequest,
  reassignTaskSuccess,
  reassignTaskFailure,
} from '../redux/tasks-slice';
import { Task, TaskCreateDTO, TaskFilters, TaskStatus, TaskUpdateDTO } from '../types/tasks.types';
import { wsEventReceived } from '@/features/notifications/redux/slices/notificationsSlice';
import {
  EntityType,
  NotificationStatus,
  NotificationType,
} from '@/features/notifications/types/notification.types';

type CreateTaskPayload = { data: TaskCreateDTO; desiredStatus?: TaskStatus };

const getErrorMessage = (error: unknown): string => {
  if (error && typeof error === 'object' && 'response' in error) {
    const axiosError = error as any;
    const response = axiosError.response;
    
    if (response?.data) {
      if (typeof response.data === 'string') {
        return response.data;
      }
      if (response.data.message) {
        return response.data.message;
      }
      if (response.data.errors && Array.isArray(response.data.errors)) {
        return response.data.errors.map((e: any) => 
          `${e.field || ''}: ${e.message || e}`
        ).join(', ');
      }
      if (typeof response.data === 'object' && response.data.error) {
        return response.data.error;
      }
      try {
        return JSON.stringify(response.data);
      } catch {
        return `Error ${response.status}: ${response.statusText}`;
      }
    }
    
    return `Error ${response?.status || 'desconocido'}: ${response?.statusText || 'Error en la petición'}`;
  }
  
  if (error instanceof Error) {
    return error.message;
  }
  
  if (typeof error === 'string') {
    return error;
  }
  
  return 'Ha ocurrido un error inesperado';
};

const selectCurrentUser = (state: any) => state.auth?.user;

function createNotificationPayload(
  type: NotificationType,
  title: string,
  message: string,
  taskId: string,
  userId?: string,
) {
  const timestamp = new Date().toISOString();
  const randomId =
    typeof crypto !== 'undefined' && crypto.randomUUID
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;

  return {
    id: randomId,
    title,
    message,
    type,
    status: NotificationStatus.UNREAD,
    userId: userId || '',
    entityType: EntityType.TASK,
    entityId: taskId,
    metadata: {
      timestamp,
      source: 'task-module',
    },
    createdAt: timestamp,
  };
}

function* notifyTaskCreated(task: Task) {
  if (!task) return;
  const user = yield select(selectCurrentUser);
  const assigneeName = task.assignedTo
    ? `${task.assignedTo.firstName} ${task.assignedTo.lastName}`.trim()
    : '';
  const message = assigneeName
    ? `Se ha asignado la tarea: "${task.title}"`
    : `Nueva tarea creada: "${task.title}"`;
  const notification = createNotificationPayload(
    NotificationType.TASK_CREATED,
    'Nueva tarea creada',
    message,
    task.id,
    user?.id,
  );
  yield put(
    wsEventReceived({
      type: 'notification_created',
      data: notification,
      timestamp: new Date().toISOString(),
    }),
  );
}

function* notifyTaskCompleted(task: Task) {
  if (!task) return;
  const user = yield select(selectCurrentUser);
  const assigneeName = task.assignedTo
    ? `${task.assignedTo.firstName} ${task.assignedTo.lastName}`.trim()
    : 'un miembro del equipo';
  const notification = createNotificationPayload(
    NotificationType.TASK_COMPLETED,
    'Tarea completada',
    `La tarea "${task.title}" ha sido completada por ${assigneeName}`,
    task.id,
    user?.id,
  );
  yield put(
    wsEventReceived({
      type: 'notification_created',
      data: notification,
      timestamp: new Date().toISOString(),
    }),
  );
}

function* notifyTaskReassigned(task: Task) {
  if (!task || !task.assignedTo) return;
  const user = yield select(selectCurrentUser);
  const notification = createNotificationPayload(
    NotificationType.TASK_ASSIGNED,
    'Tarea reasignada',
    `Se te ha asignado la tarea: "${task.title}"`,
    task.id,
    user?.id,
  );
  yield put(
    wsEventReceived({
      type: 'notification_created',
      data: notification,
      timestamp: new Date().toISOString(),
    }),
  );
}

function* handleFetchTasks(action: PayloadAction<TaskFilters | undefined>) {
  try {
    const filters = action.payload || {};
    const response: TaskResponse = yield call(tasksApiService.getTasks, filters);
    yield put(fetchTasksSuccess(response));
  } catch (error) {
    const errorMessage = getErrorMessage(error);
    yield put(fetchTasksFailure(errorMessage));
  }
}

function* handleFetchTaskById(action: PayloadAction<string>) {
  try {
    const task: Task = yield call(tasksApiService.getTaskById, action.payload);
    yield put(fetchTaskByIdSuccess(task));
  } catch (error) {
    yield put(fetchTaskByIdFailure(getErrorMessage(error)));
  }
}

function* handleCreateTask(action: PayloadAction<CreateTaskPayload>) {
  try {
    const { data, desiredStatus } = action.payload;
    let task: Task = yield call(tasksApiService.createTask, data);

    // Cambiar estado si se solicitó uno distinto al pendiente
    if (desiredStatus && desiredStatus !== 'pending' && desiredStatus !== task.status) {
      task = yield call(tasksApiService.changeStatus, task.id, desiredStatus);
    }

    yield put(createTaskSuccess(task));
    yield call(notifyTaskCreated, task);

    if (task.status === 'completed') {
      yield call(notifyTaskCompleted, task);
    }
  } catch (error) {
    const errorMessage = getErrorMessage(error);
    yield put(createTaskFailure(errorMessage));
  }
}

function* handleUpdateTask(action: PayloadAction<{ id: string; data: TaskUpdateDTO }>) {
  try {
    const { id, data } = action.payload;
    const task: Task = yield call(tasksApiService.updateTask, id, data);
    yield put(updateTaskSuccess(task));
  } catch (error) {
    yield put(updateTaskFailure(getErrorMessage(error)));
  }
}

function* handleDeleteTask(action: PayloadAction<string>) {
  try {
    yield call(tasksApiService.deleteTask, action.payload);
    yield put(deleteTaskSuccess(action.payload));
  } catch (error) {
    yield put(deleteTaskFailure(getErrorMessage(error)));
  }
}

function* handleChangeTaskStatus(action: PayloadAction<{ id: string; status: TaskStatus }>) {
  try {
    const { id, status } = action.payload;
    const task: Task = yield call(tasksApiService.changeStatus, id, status);
    yield put(changeTaskStatusSuccess(task));
    if (task.status === 'completed') {
      yield call(notifyTaskCompleted, task);
    }
  } catch (error) {
    yield put(changeTaskStatusFailure(getErrorMessage(error)));
  }
}

function* handleChangeTaskPriority(action: PayloadAction<{ id: string; priority: string }>) {
  try {
    const { id, priority } = action.payload;
    const task: Task = yield call(tasksApiService.changePriority, id, priority);
    yield put(changeTaskPrioritySuccess(task));
  } catch (error) {
    yield put(changeTaskPriorityFailure(getErrorMessage(error)));
  }
}

function* handleFetchStats() {
  try {
    const stats = yield call(tasksApiService.getStats);
    yield put(fetchStatsSuccess(stats));
  } catch (error) {
    yield put(fetchStatsFailure(getErrorMessage(error)));
  }
}

function* handleFetchOverdueTasks() {
  try {
    const tasks = yield call(tasksApiService.getOverdueTasks);
    yield put(fetchOverdueTasksSuccess(tasks));
  } catch (error) {
    yield put(fetchOverdueTasksFailure(getErrorMessage(error)));
  }
}

function* handleReassignTask(action: PayloadAction<{ id: string; assignedToId: string }>) {
  try {
    const { id, assignedToId } = action.payload;
    const task: Task = yield call(tasksApiService.reassignTask, id, assignedToId);
    yield put(reassignTaskSuccess(task));
    yield call(notifyTaskReassigned, task);
  } catch (error) {
    yield put(reassignTaskFailure(getErrorMessage(error)));
  }
}

export function* watchTasks() {
  yield all([
    takeLatest(fetchTasksRequest.type, handleFetchTasks),
    takeLatest(fetchTaskByIdRequest.type, handleFetchTaskById),
    takeLatest(createTaskRequest.type, handleCreateTask),
    takeLatest(updateTaskRequest.type, handleUpdateTask),
    takeLatest(deleteTaskRequest.type, handleDeleteTask),
    takeLatest(changeTaskStatusRequest.type, handleChangeTaskStatus),
    takeLatest(changeTaskPriorityRequest.type, handleChangeTaskPriority),
    takeLatest(fetchStatsRequest.type, handleFetchStats),
    takeLatest(fetchOverdueTasksRequest.type, handleFetchOverdueTasks),
    takeLatest(reassignTaskRequest.type, handleReassignTask),
  ]);
}

export function* tasksSaga() {
  yield watchTasks();
}



